import type { CourseSlug } from './courses.js';
import type { MenuTypeSlug } from './menuTypes.js';
import { COURSE_ORDER } from './courseOrder.js';
import { COURSE_LABELS } from './courseLabels.js';

export interface CourseItem {
  name: string;
  description: string;
  price: string;
  isCourseNote: boolean;
  /** Free-text allergen declaration (Décret n°2015-447). Omitted for historical snapshot items. */
  allergens?: string;
}

export interface CourseSection {
  course: CourseSlug;
  /** Heading text, or `null` to render the course without a heading (Menu du Jour). */
  label: string | null;
  items: CourseItem[];
}

export interface GroupableDish {
  name: string;
  description: string;
  price: string;
  isCourseNote: boolean;
  course: CourseSlug | null;
  allergens?: string;
  /** Rank within the course section (lower prints first). Defaults to 0. */
  coursePosition?: number;
}

/**
 * Group dishes for a menu type into ordered course sections, mirroring the WP
 * `hotel_site_query_menu_grouped`: ordered by COURSE_ORDER[menuType], labelled
 * by COURSE_LABELS[menuType], with courses absent from that menu's order dropped
 * (strict). Caller is responsible for pre-filtering (active, not deleted, type).
 */
export function groupByCourse(dishes: GroupableDish[], menuType: MenuTypeSlug): CourseSection[] {
  const order = COURSE_ORDER[menuType];
  const labels = COURSE_LABELS[menuType];
  // Track each item with its rank + arrival index so ties keep input order (stable sort).
  const buckets = new Map<CourseSlug, { item: CourseItem; pos: number; seq: number }[]>();
  let seq = 0;

  for (const d of dishes) {
    const course = d.course;
    if (!course) continue;
    if (order[course] === undefined) continue; // course not part of this menu
    const items = buckets.get(course) ?? [];
    items.push({
      item: {
        name: d.name,
        description: d.description,
        price: d.price,
        isCourseNote: d.isCourseNote,
        allergens: d.allergens,
      },
      pos: d.coursePosition ?? 0,
      seq: seq++,
    });
    buckets.set(course, items);
  }

  return [...buckets.keys()]
    .sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
    .map((course) => {
      const lbl = labels[course];
      const entries = (buckets.get(course) ?? []).sort((a, b) => a.pos - b.pos || a.seq - b.seq);
      return {
        course,
        label: lbl === undefined ? null : lbl,
        items: entries.map((e) => e.item),
      };
    });
}
